/**
 * CursorRangePagination
 *
 * Prev/Next pagination for cursor-paginated lists (key > token, LIMIT
 * pageSize+1) that cannot jump to an arbitrary page number the way
 * PagePagination (offset-based) can. Renders "Showing {rangeStart}-{rangeEnd}
 * of {total}" when the backend's widened peek found the true end of the
 * result set, or "Showing {rangeStart}-{rangeEnd} of many" when it hit the
 * peek cap (see server/internal/repository/pagination.go).
 *
 * Backend contract: the list endpoint returns, alongside its page of rows,
 * remainingCount (number) and remainingIsExact (boolean) — either as fields
 * on a nested pageInfo object (protobuf messages that embed
 * elaccounts.common.PageInfo) or as flat top-level fields (billing.proto's
 * ListPartiesResponse/ListInvoicesResponse, and the hand-rolled Payments /
 * Purchase Orders / Customer Purchase Orders JSON responses). Map whichever
 * shape your endpoint uses onto this component's `total` / `isExact` props:
 *
 *   const rangeStart = rowsShownBeforeThisPage + 1
 *   const rangeEnd = rowsShownBeforeThisPage + loadedCount
 *   const total = rangeEnd + remainingCount
 *   const isExact = remainingIsExact
 *
 * Prev/Next token stack: cursor pagination only carries a "next" token, so
 * going back requires the CALLER to keep a client-side stack of the page
 * tokens it has already visited (push the current token before requesting
 * Next; pop it and re-fetch with the previous token on Prev). This component
 * does not manage that stack — it only renders hasPrev/hasNext and forwards
 * onPrev/onNext clicks.
 */

import { Button, Skeleton } from '@elsapiens/ui'

export interface CursorRangePaginationProps {
  /** 1-based index of the first item shown on the current page. */
  rangeStart: number
  /** 1-based index of the last item shown on the current page. */
  rangeEnd: number
  /**
   * Best-known total: rangeEnd + remainingCount from the backend's widened
   * peek. Only rendered when isExact is true.
   */
  total: number
  /**
   * True when the backend's peek reached the true end of the filtered
   * result set (total is exact). False when it hit its cap — renders
   * "of many" instead of the number.
   */
  isExact: boolean
  /** Whether a previous page exists (caller's token stack is non-empty). */
  hasPrev: boolean
  /** Whether at least one more page exists after this one. */
  hasNext: boolean
  /** Whether a page navigation is currently in flight. */
  isLoading?: boolean
  /** Show shimmer placeholder instead of real content. */
  shimmer?: boolean
  /** Navigate to the previous page (caller pops its token stack). */
  onPrev: () => void
  /** Navigate to the next page (caller pushes the current token, then fetches with nextPageToken). */
  onNext: () => void
}

// Match Table's edge-to-edge pattern (same as Pagination / PagePagination).
const edgeToEdgeStyle = {
  marginLeft: 'calc(var(--el-table-spacer-width) * -1)',
  marginRight: 'calc(var(--el-table-spacer-width) * -1)',
  marginBottom: 'calc(var(--el-table-spacer-width) * -1)',
  width: 'calc(100% + var(--el-table-spacer-width) * 2)',
  paddingLeft: 'var(--el-table-spacer-width)',
  paddingRight: 'var(--el-table-spacer-width)',
  paddingTop: 'var(--el-layout-gap-sm)',
  paddingBottom: 'var(--el-layout-gap-sm)',
}

/** "Showing {rangeStart}-{rangeEnd} of {total|many}" text only — no Prev/Next chrome. */
export function CursorRangeText({ rangeStart, rangeEnd, total, isExact }: Pick<
  CursorRangePaginationProps, 'rangeStart' | 'rangeEnd' | 'total' | 'isExact'
>): JSX.Element {
  return (
    <span className="text-sm text-foreground-secondary">
      Showing {rangeStart}&ndash;{rangeEnd} of {isExact ? total : 'many'}
    </span>
  )
}

export function CursorRangePagination({
  rangeStart,
  rangeEnd,
  total,
  isExact,
  hasPrev,
  hasNext,
  isLoading = false,
  shimmer = false,
  onPrev,
  onNext,
}: CursorRangePaginationProps): JSX.Element | null {
  if (shimmer) {
    return (
      <div className="flex items-center justify-between border-t border-border" style={edgeToEdgeStyle}>
        <Skeleton height="1em" width="120px" />
      </div>
    )
  }

  if (rangeEnd === 0 && !hasNext && !hasPrev) { return null }

  return (
    <div className="flex items-center justify-between border-t border-border" style={edgeToEdgeStyle}>
      <CursorRangeText isExact={isExact} rangeEnd={rangeEnd} rangeStart={rangeStart} total={total} />
      <div className="flex items-center gap-3">
        <Button
          disabled={isLoading || !hasPrev}
          size="sm"
          variant="secondary"
          onClick={onPrev}
        >
          Previous
        </Button>
        <Button
          disabled={isLoading || !hasNext}
          size="sm"
          variant="secondary"
          onClick={onNext}
        >
          {isLoading ? 'Loading...' : 'Next'}
        </Button>
      </div>
    </div>
  )
}
